import type {
  CustomerStatusInput,
  PlatformVersionInput,
  TenantHealthInput,
  VersionFields,
} from "./types";
import { LABEL } from "./terminology";

/** Deploy metadata: missing / unavailable → Not available (never Unknown). */
export function deployMetaValue(
  raw: string | null | undefined,
  ...fallbacks: Array<string | null | undefined>
): string {
  for (const candidate of [raw, ...fallbacks]) {
    const text = (candidate ?? "").trim();
    if (!text) continue;
    const lower = text.toLowerCase();
    if (
      lower === "unknown" ||
      lower === "unavailable" ||
      lower === "none" ||
      lower === "null" ||
      lower === "n/a"
    ) {
      continue;
    }
    return text;
  }
  return LABEL.notAvailable;
}

/**
 * Map host APP_ENV-style values to Prod/Staging/Dev when recognizable.
 * Unknown / empty → Not available (never invent a tenant environment).
 */
export function deriveDeploymentEnvironment(
  raw: string | null | undefined,
): string {
  const text = (raw ?? "").trim();
  if (!text) return LABEL.notAvailable;
  const lower = text.toLowerCase();
  if (
    lower === "unknown" ||
    lower === "unavailable" ||
    lower === "none" ||
    lower === "null" ||
    lower === "n/a"
  ) {
    return LABEL.notAvailable;
  }
  if (lower === "prod" || lower === "production") return "Production";
  if (lower === "staging" || lower === "stage" || lower === "stg") return "Staging";
  if (lower === "dev" || lower === "development" || lower === "local") {
    return "Development";
  }
  return text;
}

/**
 * Version fields are separate — never silently substitute (Phase 6).
 * Missing → "Not available" (not Unknown).
 */
export function deriveVersionFields(
  customer: Pick<CustomerStatusInput, "image_tag" | "provision_completed_at">,
  tenantHealth: Pick<TenantHealthInput, "platform_version"> | null,
  platformMeta?: PlatformVersionInput | null,
): VersionFields {
  const platform = (tenantHealth?.platform_version ?? "").trim();
  const tag = (customer.image_tag ?? "").trim();
  const cp = platformMeta?.control_plane;

  return {
    platformVersion: deployMetaValue(platform, cp?.version),
    dockerImage: deployMetaValue(tag, cp?.image_tag, platformMeta?.image_tag),
    gitCommit: deployMetaValue(
      platformMeta?.git_commit,
      cp?.git_commit,
    ),
    imageTag: deployMetaValue(tag, cp?.image_tag, platformMeta?.image_tag),
    buildTime: deployMetaValue(
      platformMeta?.build_timestamp,
      cp?.build_timestamp,
    ),
    deploymentTime: deployMetaValue(customer.provision_completed_at),
    branch: deployMetaValue(platformMeta?.git_branch, cp?.git_branch),
    deploymentEnvironment: deriveDeploymentEnvironment(platformMeta?.environment),
  };
}

/**
 * Overview version column: platform version only.
 * Do not fall back to image_tag (that conflates two concepts).
 */
export function deriveOverviewVersion(
  _customer: CustomerStatusInput,
  tenantHealth: Pick<TenantHealthInput, "platform_version"> | null,
): string {
  const platform = (tenantHealth?.platform_version ?? "").trim();
  return platform || LABEL.notAvailable;
}
